import { useState, useMemo } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  OutlinedInput,
  IconButton,
  ThemeProvider,
} from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { CAP_CATEGORY, CULTURE_TYPE, getCultureTypeLabels } from 'utils/GlobalVariables';
import { getCultureSeasonLabels, EnumCultureSeason } from 'components/generics/Interface/ICulture';
import { generalSelectTheme, invertedButtonTheme } from 'assets/styles/themes/tabs_mui';
import { generalTheme } from 'assets/styles/themes/general_theme';
import { useTranslation } from 'react-i18next';

const createEmptyFilter = () => ({
  id: Date.now(),
  targets: [],
  property: '',
  values: [],
});

const getFilterProperties = (cultures, activeFilters = [], currentTargets = [], t) => {
  const hasIntermediateCulture = cultures?.some(
    (culture) => culture.cultureType === CULTURE_TYPE.intermediate,
  );

  // Get already applied filters (excluding 'id' filter)
  const appliedFilters = activeFilters.filter(
    (filter) =>
      filter.property &&
      filter.property !== 'id' &&
      filter.values.length > 0 &&
      filter.targets.some((target) => currentTargets.includes(target)),
  );

  // Filter cultures based on already applied filters
  const filteredCultures = cultures?.filter((culture) =>
    appliedFilters.every((filter) => filter.values.includes(culture[filter.property])),
  );

  const properties = [
    {
      field: 'cultureSeason',
      label: t('column.season'),
      options: Object.values(EnumCultureSeason).map((value) => ({
        value,
        label: getCultureSeasonLabels(t)[value],
      })),
    },
    {
      field: 'capCategory',
      label: t('form.capCategoryLabel'),
      options: Object.entries(CAP_CATEGORY).map(([key, label]) => ({
        value: label,
        label,
      })),
    },
    {
      field: 'id',
      label: t('common.culture'),
      options: (filteredCultures || cultures).map((culture) => ({
        value: culture.id,
        label: culture.name,
      })),
    },
  ];

  if (hasIntermediateCulture) {
    properties.unshift({
      field: 'cultureType',
      label: t('column.cultureType'),
      options: Object.values(CULTURE_TYPE).map((value) => ({
        value,
        label: getCultureTypeLabels(t)[value],
      })),
    });
  }

  return properties;
};

const getValidCultureIds = (filtersList, cultureList, currentTarget) => {
  const appliedFilters = filtersList.filter(
    (filter) =>
      filter.property &&
      filter.property !== 'id' &&
      filter.values.length > 0 &&
      filter.targets.some((target) => currentTarget.includes(target)),
  );
  return cultureList
    ?.filter((culture) =>
      appliedFilters.every((filter) => filter.values.includes(culture[filter.property])),
    )
    .map((culture) => culture.id);
};

const cleanCultureFilter = (filtersList, cultureList, currentTarget) => {
  const validIds = getValidCultureIds(filtersList, cultureList, currentTarget);
  return filtersList.map((filter) => {
    return filter.property === 'id' && filter.targets.includes(currentTarget[0])
      ? { ...filter, values: filter.values.filter((value) => validIds?.includes(value)) }
      : filter;
  });
};

const buildPropertyTargets = (filtersList, currentFilterId) =>
  filtersList
    .filter((filter) => filter.id !== currentFilterId && filter.property)
    .reduce((accumulator, filter) => {
      const existing = accumulator.find((entry) => entry.property === filter.property);
      const targets = Array.isArray(filter.targets) ? filter.targets : [];
      if (existing) {
        targets.forEach((target) => {
          if (!existing.targets.includes(target)) existing.targets.push(target);
        });
      } else {
        accumulator.push({ property: filter.property, targets: [...targets] });
      }
      return accumulator;
    }, []);

const isPropertyAllowed = (propertyField, currentTargets, propertyTargets) => {
  if (!propertyField) return true;

  const targetsArray = currentTargets ?? [];
  const propertyTargetEntry = propertyTargets.find((entry) => entry.property === propertyField);

  if (!propertyTargetEntry || propertyTargetEntry.targets.length === 0 || !targetsArray.length) {
    return true;
  }

  return !targetsArray.some((target) => propertyTargetEntry.targets.includes(target));
};

export const createCustomRotateEffectsFilterPanel = (propsRef) => {
  return function CustomRotateEffectsFilterPanel() {
    const apiRef = useGridApiContext();
    const { t } = useTranslation();

    const { activeFilters, cultures } = propsRef.current;
    // Set used here to have a object with unique property
    const uniqueFilter = new Set(activeFilters.map((filter) => filter.property && filter.targets));

    // Initialiser avec les filtres sauvegardés (avec clone profond pour éviter les mutations)
    const [filters, setFilters] = useState(() => {
      if (activeFilters && activeFilters.length > 0) {
        return activeFilters.map((filterDefinition) => ({
          ...filterDefinition,
          targets: [...filterDefinition.targets],
          values: [...filterDefinition.values],
        }));
      }
      return [createEmptyFilter()];
    });

    const filterProperties = useMemo(
      () => getFilterProperties(cultures, filters, [], t),
      [cultures, filters, t],
    );

    const getAvailableValues = (propertyField, filterId) => {
      if (!propertyField) return [];

      const currentFilter = filters.find((filter) => filter.id === filterId);
      const properties = getFilterProperties(cultures, filters, currentFilter?.targets ?? [], t);

      return properties.find((property) => property.field === propertyField)?.options ?? [];
    };

    const getAvailableProperties = (currentFilterId) => {
      const currentFilter = filters.find((filter) => filter.id === currentFilterId);
      const currentTargets = currentFilter?.targets ?? [];
      const propertyTargets = buildPropertyTargets(filters, currentFilterId);

      return filterProperties.filter((propertyDefinition) =>
        isPropertyAllowed(propertyDefinition.field, currentTargets, propertyTargets),
      );
    };

    const updateFilter = (filterId, changes, skipClean = false) => {
      const currentFilter = filters.find((filter) => filter.id === filterId);
      setFilters((previousFilters) => {
        const updatedFilters = previousFilters.map((filter) =>
          filter.id === filterId ? { ...filter, ...changes } : filter,
        );
        return skipClean
          ? updatedFilters
          : cleanCultureFilter(updatedFilters, cultures, currentFilter?.targets ?? []);
      });
    };

    const handleFilterTargetsChange = (filterId, event) => {
      const newTargets = event.target.value;

      setFilters((previousFilters) => {
        const updatedFilters = previousFilters.map((filter) =>
          filter.id === filterId ? { ...filter, targets: newTargets } : filter,
        );

        const propertyTargets = buildPropertyTargets(updatedFilters, filterId);

        return updatedFilters.map((filter) => {
          if (filter.id !== filterId || !filter.property) return filter;

          return isPropertyAllowed(filter.property, newTargets, propertyTargets)
            ? filter
            : { ...filter, property: '', values: [] };
        });
      });
    };

    const handlePropertyChange = (filterId, event) =>
      updateFilter(filterId, { property: event.target.value, values: [] });

    const handleValuesChange = (filterId, event) => {
      const currentFilter = filters.find((filter) => filter.id === filterId);
      updateFilter(filterId, { values: event.target.value }, currentFilter?.property === 'id');
    };

    const handleAddFilter = () =>
      setFilters((previousFilters) => [...previousFilters, createEmptyFilter()]);

    const handleRemoveFilter = (filterId) =>
      setFilters((previousFilters) =>
        previousFilters.length === 1
          ? [createEmptyFilter()]
          : previousFilters.filter((filter) => filter.id !== filterId),
      );

    const handleApply = () => {
      propsRef.current.updateFilters(filters);
      apiRef.current.hideFilterPanel();
    };

    const handleReset = () => {
      setFilters([createEmptyFilter()]);
      propsRef.current.resetFilters();
      apiRef.current.hideFilterPanel();
    };

    const isApplyDisabled = filters.some(
      (filter) => filter.targets.length === 0 || !filter.property || filter.values.length === 0,
    );

    // Disable the "Add" button if all properties are used
    const isAddDisabled = uniqueFilter.size >= filterProperties.length * 2;

    return (
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 700 }}>
        {filters.map((filter) => (
          <Box
            key={filter.id}
            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}
          >
            <ThemeProvider theme={generalSelectTheme}>
              <FormControl size="small" sx={{ width: 170 }}>
                <InputLabel>{t('filterPanel.filterOn')}</InputLabel>
                <Select
                  className="filter-panel__select-targets"
                  multiple
                  value={filter.targets}
                  onChange={(event) => handleFilterTargetsChange(filter.id, event)}
                  input={<OutlinedInput label={t('filterPanel.filterOn')} />}
                  renderValue={(selected) =>
                    selected
                      .map((value) => (value === 'rows' ? t('common.rows') : t('common.columns')))
                      .join(', ')
                  }
                >
                  <MenuItem value="rows">
                    <Checkbox checked={filter.targets.includes('rows')} />
                    <ListItemText primary={t('common.rows')} />
                  </MenuItem>
                  <MenuItem value="columns">
                    <Checkbox checked={filter.targets.includes('columns')} />
                    <ListItemText primary={t('common.columns')} />
                  </MenuItem>
                </Select>
              </FormControl>
            </ThemeProvider>

            <ThemeProvider theme={generalSelectTheme}>
              <FormControl size="small" sx={{ width: 170 }} disabled={!filter.targets.length}>
                <InputLabel>{t('filterPanel.property')}</InputLabel>
                <Select
                  value={filter.property}
                  onChange={(event) => handlePropertyChange(filter.id, event)}
                  label={t('filterPanel.property')}
                >
                  {getAvailableProperties(filter.id).map((property) => (
                    <MenuItem key={property.field} value={property.field}>
                      {property.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ThemeProvider>

            <ThemeProvider theme={generalSelectTheme}>
              <FormControl size="small" sx={{ width: 275 }} disabled={!filter.property}>
                <InputLabel>{t('filterPanel.values')}</InputLabel>
                <Select
                  multiple
                  value={filter.values}
                  onChange={(event) => handleValuesChange(filter.id, event)}
                  input={<OutlinedInput label={t('filterPanel.values')} />}
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const option = getAvailableValues(filter.property, filter.id).find(
                          (availableOption) => availableOption.value === value,
                        );
                        return option?.label || value;
                      })
                      .join(', ')
                  }
                >
                  {getAvailableValues(filter.property, filter.id).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={filter.values.includes(option.value)} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ThemeProvider>

            <IconButton size="small" onClick={() => handleRemoveFilter(filter.id)} color="error">
              <DeleteIcon sx={{ paddingLeft: 0 }} />
            </IconButton>
          </Box>
        ))}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <ThemeProvider theme={invertedButtonTheme}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddFilter}
              disabled={isAddDisabled}
            >
              {t('filterPanel.addFilter')}
            </Button>
          </ThemeProvider>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <ThemeProvider theme={invertedButtonTheme}>
              <Button variant="outlined" size="small" onClick={handleReset}>
                {t('filterPanel.reset')}
              </Button>
            </ThemeProvider>
            <ThemeProvider theme={generalTheme}>
              <Button
                variant="contained"
                size="small"
                onClick={handleApply}
                disabled={isApplyDisabled}
              >
                {t('common.validate')}
              </Button>
            </ThemeProvider>
          </Box>
        </Box>
      </Box>
    );
  };
};
